import { useEffect, useRef, useState } from "react";
import { ALargeSmall, Bookmark, Play, X } from "lucide-react";
import Highlighter from "react-highlight-words";
import { marked } from "marked";
import parse, { domToReact, Element } from "html-react-parser";

import * as Api from "@modules/api";
import { MenuIcon } from "@components/icons/MenuIcon";
import { ReadingCarousel } from "@components/items/display/ReadingCarousel";
import { ScrollArea } from "@ui/scroll-area";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import { useAudioPlayer } from "@modules/content/AudioPlayerContext";
import { useIntersectionObserver } from "@utils/hooks/useIntersectionObserver";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@ui/menubar";
import { Button } from "@ui/button";
import TranslatedText from "@components/items/display/TranslatedText";
import { ItemContentOutputWithProgress } from "@lib/stores/content/types";
import { addBookmark, removeBookmark } from "@lib/stores";
import { useHighlightedText } from "@modules/content/hooks/useHighlightedText";
import { Modal } from "@components/items/display/Modal";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { ReaderStyle } from "./ContentDetails";
import BasicSlider from "@components/items/display/Slider";
import MarkdownView from "react-showdown";
import React from "react";
import { useB2B } from "@modules/content/B2BContext";
import { useLanguage } from "@modules/localizations/LanguageContext";
import { Direction } from "@utils/types/common";

interface ReaderProps {
  title: string | undefined;
  onClose: () => void;
  onListenToTextClick: () => void;
  style: ReaderStyle;
  onStyleChange: (style: ReaderStyle) => void;
}

export const Reader = ({
  title,
  onClose,
  onListenToTextClick,
  style,
  onStyleChange,
}: ReaderProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const scrollableElementRef = useRef<HTMLDivElement>(null);
  const scrollDirectionRef = useRef<"up" | "down" | null>(null);

  let lastScrollTop = scrollableElementRef.current?.scrollTop;

  const [chaptersWithOptimisticBookmark, setChaptersWithOptimisticBookmark] =
    useState<ItemContentOutputWithProgress[]>([]);
  // const [removeHighlightModal, setRemoveHighlightModal] = useState<{
  //   isOpen: boolean;
  //   id: number | undefined;
  //   text: string | undefined;
  // }>({ isOpen: false, id: undefined, text: undefined });

  const { b2B } = useB2B();
  const { direction } = useLanguage();

  const {
    chapters,
    firstPlayingChapterId,
    onPlayingChapterSelect,
    playingChapterId,
    onNextChapterStart,
    onPlaying,
    selectedPlayerContent,
    forcedPlayingChapterId,
  } = useSelectedPlayerContent();

  const { load, unload, changeIsAutoStart } = useAudioPlayer();

  // const {
  //   axis,
  //   isPopoverShown,
  //   clearSelection,
  //   createHighlightedText,
  //   removeHighlightedText,
  //   hidePopover,
  //   textRangesSubmitted,
  // } = useHighlightedText({
  //   selectedContentId: selectedPlayerContent.id,
  //   playingChapterId: playingChapterId,
  //   parentElementRef: scrollableElementRef,
  //   parentElementScrollDirRef: scrollDirectionRef,
  // });

  const endOfPlayingChapterRef = useIntersectionObserver(() => {
    onPlaying(1, Api.Server.PlayingMode.ReadingPage);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  });

  const firstPlayingChapterIndex = chapters.findIndex(
    (c) => c.id === firstPlayingChapterId
  );

  const playingChapter = chapters.find((c) => c.id === playingChapterId);
  const playingChapterOptimistic = chaptersWithOptimisticBookmark.find(
    (c) => c.id === playingChapterId
  );

  const playingChapterIndex = chapters.findIndex(
    (c) => c.id === playingChapterId
  );

  const onAudioSelect = (id: string | undefined) => {
    unload();

    const selectedChapterSignedUrl = chapters.find(
      (c) => c.id === id
    )?.signedAudioUrl;

    if (selectedChapterSignedUrl) {
      setTimeout(() => {
        load(selectedChapterSignedUrl);
      }, 100);
    }
  };

  useEffect(() => {
    const scrollableElement = scrollableElementRef.current;

    if (scrollableElement) {
      const scrollHeight = scrollableElement.scrollHeight;
      const clientHeight = scrollableElement.clientHeight;

      intervalRef.current = setInterval(() => {
        onPlaying(
          scrollHeight > clientHeight
            ? Math.ceil(scrollableElement.scrollTop) /
                Math.ceil(scrollHeight - clientHeight)
            : 1,
          Api.Server.PlayingMode.ReadingPage
        );
      }, 15000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [playingChapterId]);

  useEffect(() => {
    setChaptersWithOptimisticBookmark(chapters);
  }, [chapters.length]);

  return (
    <div className="relative flex h-full flex-col items-start justify-start">
      <div
        className={`absolute top-0 flex items-center justify-center gap-x-2 ltr:right-0 rtl:left-0 ${
          style.backgroundColor ? "text-white" : "text-black dark:text-white"
        }`}
      >
        <ReaderMenu
          onListenToTextClick={onListenToTextClick}
          isBookmarked={Boolean(playingChapterOptimistic?.isBookmarked)}
          onAddBookmarkClick={() => {
            setChaptersWithOptimisticBookmark((chapters) => {
              return chapters.map((c) => {
                if (c.id === playingChapterId) {
                  return {
                    ...c,
                    isBookmarked: true,
                  };
                } else {
                  return c;
                }
              });
            });

            addBookmark(
              {
                containerContentEntityId: selectedPlayerContent.id,
                contentEntityId: playingChapterId,
              },
              () => {
                setChaptersWithOptimisticBookmark((chapters) => {
                  return chapters.map((c) => {
                    return {
                      ...c,
                      isBookmarked: false,
                    };
                  });
                });
              }
            );
          }}
          onRemoveBookmarkClick={() => {
            setChaptersWithOptimisticBookmark((chapters) => {
              return chapters.map((c) => {
                if (c.id === playingChapterId) {
                  return {
                    ...c,
                    isBookmarked: false,
                  };
                } else {
                  return c;
                }
              });
            });

            removeBookmark({ contentEntityId: playingChapterId }, () => {
              setChaptersWithOptimisticBookmark((chapters) => {
                return chapters.map((c) => {
                  return {
                    ...c,
                    isBookmarked: true,
                  };
                });
              });
            });
          }}
          textSettings={style}
          onTextSettingsChange={onStyleChange}
          isB2B={Boolean(b2B?.isB2B)}
          direction={direction}
        />
        <X className="h-5 w-5 cursor-pointer" onClick={onClose} />
      </div>
      <h1
        className={`w-2/3 pb-4 text-[20px] font-medium ${
          style.backgroundColor ? "text-white" : "text-black dark:text-white"
        }`}
        style={{
          fontFamily: style.fontType.value,
        }}
      >
        {playingChapter?.title ?? title}
      </h1>
      <div className={`mb-auto h-full w-full pb-24`}>
        {!forcedPlayingChapterId ? (
          <ReadingCarousel
            items={chapters.map((c) => {
              return (
                <div
                  key={c.id}
                  ref={c.id === playingChapterId ? scrollableElementRef : null}
                  className={`cus-scrollbar-w-none relative flex h-full flex-col items-start justify-start overflow-y-auto ${
                    style.backgroundColor
                      ? "text-white"
                      : "text-black dark:text-white"
                  } ${
                    c.id === playingChapterId ? "select-auto" : "select-none"
                  }`}
                  dir={direction}
                  onScroll={(event: React.UIEvent<HTMLDivElement>) => {
                    const target = event.currentTarget;

                    const currentScrollTop = target.scrollTop;

                    if (currentScrollTop > (lastScrollTop || 0)) {
                      scrollDirectionRef.current = "down";
                    } else if (currentScrollTop < (lastScrollTop || 0)) {
                      scrollDirectionRef.current = "up";
                    }

                    lastScrollTop = currentScrollTop;
                  }}
                >
                  {/* {isPopoverShown && (
                    <div
                      className={`absolute bg-white text-black flex items-center justify-evenly divide-x rounded-lg p-2 w-[100px] shadow-lg z-10 cursor-pointer`}
                      style={{ left: `${axis.x}px`, top: `${axis.y}px` }}
                      role="presentation"
                      onMouseDown={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      onClick={() => {
                        createHighlightedText(c.textId || 0);
                      }}
                    >
                      تظليل
                    </div>
                  )} */}
                  <div
                    className="flex-grow py-[25px]"
                    style={{
                      fontSize: style.fontSize,
                      fontFamily: style.fontType.value,
                    }}
                    // onMouseDown={() => {
                    //   if (isPopoverShown) {
                    //     clearSelection();
                    //     hidePopover();
                    //   }
                    // }}
                  >
                    <MarkdownView
                      markdown={c.text || ""}
                      options={{ tables: true, emoji: true }}
                    />
                    {/* <Highlighter
                      highlightTag={({ children, highlightIndex }) => {
                        return (
                          <mark
                            className="text-white dark:text-black cursor-pointer"
                            onClick={() => {
                              const highlight = textRangesSubmitted
                                .filter((t) => t.contentEntityId === c.id)
                                .sort((a, b) => (a.from || 0) - (b.from || 0))[
                                highlightIndex
                              ];

                              setRemoveHighlightModal({
                                isOpen: true,
                                id: highlight.id,
                                text: c.text?.slice(
                                  highlight.from,
                                  highlight.to
                                ),
                              });
                            }}
                          >
                            {children}
                          </mark>
                        );
                      }}
                      // highlightClassName="text-white dark:text-black"
                      searchWords={textRangesSubmitted
                        .filter((t) => t.contentEntityId === c.id)
                        .map((t) => {
                          return c.text?.slice(t.from, t.to) || "";
                        })}
                      autoEscape={true}
                      textToHighlight={c.text || ""}
                      
                    /> */}
                  </div>
                  <div
                    ref={
                      c.id === playingChapterId ? endOfPlayingChapterRef : null
                    }
                    className="h-[2px] w-full"
                  ></div>
                </div>
              );
            })}
            itemClassName="basis-[100%]"
            options={{
              direction,
              startIndex: Math.max(firstPlayingChapterIndex, 0),
              watchDrag: false,
            }}
            labelClassName={`absolute -bottom-12 rtl:-left-[13px] ltr:right-[13px] pt-4 flex justify-center items-center ${
              style.backgroundColor
                ? "text-white"
                : "text-black dark:text-white"
            }`}
            onNextClick={() => {
              onPlayingChapterSelect(
                chapters[Math.min(playingChapterIndex + 1, chapters.length - 1)]
                  .id,
                () => {
                  changeIsAutoStart(false);

                  onAudioSelect(
                    chapters[
                      Math.min(playingChapterIndex + 1, chapters.length - 1)
                    ].id
                  );
                }
              );

              onNextChapterStart(
                chapters[playingChapterIndex].id,
                Api.Server.PlayingMode.ReadingPage,
                1
              );
            }}
            onPreviousClick={() => {
              onPlayingChapterSelect(
                chapters[Math.max(playingChapterIndex - 1, 0)].id,
                () => {
                  changeIsAutoStart(false);

                  onAudioSelect(
                    chapters[Math.max(playingChapterIndex - 1, 0)].id
                  );
                }
              );
            }}
          />
        ) : null}
      </div>
      {/* {removeHighlightModal?.isOpen && (
        <HighlightRemoveModal
          isOpen={removeHighlightModal.isOpen}
          data={{
            id: removeHighlightModal.id,
            text: removeHighlightModal.text,
          }}
          onClose={() => {
            setRemoveHighlightModal((removeHighlightModal) => {
              return {
                ...removeHighlightModal,
                isOpen: false,
              };
            });
          }}
          onSubmit={() => {
            if (removeHighlightModal.id) {
              removeHighlightedText(removeHighlightModal.id);

              setRemoveHighlightModal((removeHighlightModal) => {
                return {
                  ...removeHighlightModal,
                  isOpen: false,
                };
              });
            }
          }}
        />
      )} */}
    </div>
  );
};

interface ReaderMenuProps {
  onListenToTextClick: () => void;
  isBookmarked: boolean;
  onAddBookmarkClick: () => void;
  onRemoveBookmarkClick: () => void;
  textSettings: ReaderStyle;
  onTextSettingsChange: (textSettings: ReaderStyle) => void;
  isB2B: boolean;
  direction: Direction;
}

const ReaderMenu = ({
  onListenToTextClick,
  isBookmarked,
  onAddBookmarkClick,
  onRemoveBookmarkClick,
  textSettings,
  onTextSettingsChange,
  isB2B,
  direction,
}: ReaderMenuProps) => {
  return (
    <Menubar className="border-none" dir={direction}>
      <MenubarMenu>
        <MenubarTrigger
          asChild
          className="cursor-pointer rounded-none focus:bg-transparent focus-visible:ring-0 data-[state=open]:bg-transparent"
        >
          <Button variant="basic" className="gap-x-10">
            <MenuIcon width={34} height={6} />
          </Button>
        </MenubarTrigger>
        <MenubarContent
          sideOffset={0}
          align="end"
          className="bg-[#efefef] text-[#646464] dark:bg-[#2D2E35] dark:text-[#898A8D]"
        >
          <MenubarItem
            inset={false}
            className="group cursor-pointer"
            onClick={() => {
              onListenToTextClick();
            }}
          >
            <div className="flex w-full items-center justify-between gap-x-2">
              <TranslatedText
                text={`content-details.reader.listen to the text`}
              />
              <Play className="h-5 w-5 cursor-pointer fill-black text-black dark:fill-white dark:text-white" />
            </div>
          </MenubarItem>
          {!isB2B ? (
            <MenubarItem
              inset={false}
              className="group cursor-pointer"
              onClick={(e) => {
                e.preventDefault();

                if (!isBookmarked) {
                  onAddBookmarkClick();
                } else {
                  onRemoveBookmarkClick();
                }
              }}
            >
              <div className="flex w-full items-center justify-between gap-x-2">
                {!isBookmarked ? (
                  <>
                    <TranslatedText
                      text={`content-details.reader.add bookmark`}
                    />
                    <Bookmark className="h-5 w-5" />
                  </>
                ) : (
                  <>
                    <TranslatedText
                      text={`content-details.reader.remove bookmark`}
                    />
                    <Bookmark
                      fill="#FD7777"
                      stroke="#FD7777"
                      className="h-5 w-5"
                    />
                  </>
                )}
              </div>
            </MenubarItem>
          ) : null}
          <MenubarSub>
            <MenubarSubTrigger className="flex items-center justify-start px-2 py-2">
              <div>
                <TranslatedText text={`content-details.reader.text settings`} />
              </div>
              <div className="ltr:ml-auto rtl:mr-auto">
                <ALargeSmall />
              </div>
            </MenubarSubTrigger>
            <MenubarSubContent
              sideOffset={8}
              className="bg-[#efefef] text-[#898A8D] dark:bg-[#2D2E35]"
            >
              <MenubarItem
                className="flex w-[176px] flex-col items-start justify-center gap-y-4 p-2"
                onSelect={(e) => e.preventDefault()}
              >
                <div className="flex w-full flex-col items-start justify-center gap-y-3">
                  <TranslatedText text={`content-details.reader.font size`} />
                  <div className="flex w-full items-center justify-center gap-x-2">
                    <span className="text-[20px]">A</span>
                    <BasicSlider
                      defaultValue={[textSettings.fontSize]}
                      min={12}
                      max={60}
                      step={1}
                      trackClassName="h-1"
                      rangeClassName="bg-primary"
                      thumbClassName="bg-primary border-primary h-4 w-4"
                      onValueChange={(value) => {
                        onTextSettingsChange({
                          ...textSettings,
                          fontSize: value[0],
                        });
                      }}
                    />
                    <span className="text-[14px]">A</span>
                  </div>
                </div>
                <div className="flex w-full flex-col items-start justify-center gap-y-3">
                  <TranslatedText text={`content-details.reader.font type`} />
                  <div className="flex w-full items-center justify-between gap-x-2 text-[12px]">
                    <TranslatedText
                      text={`content-details.reader.neo-font`}
                      className={`font-['Neo Sans Arabic'] flex h-[30px] w-[45px] cursor-pointer items-center justify-center rounded-lg border text-center ${
                        textSettings.fontType.value === "Neo Sans Arabic"
                          ? "border-primary dark:border-primary"
                          : "border-black dark:border-white"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          fontType: {
                            value: "Neo Sans Arabic",
                            label: "Neo",
                          },
                        });
                      }}
                    />
                    <TranslatedText
                      text={`content-details.reader.serif-font`}
                      className={`flex h-[30px] w-[45px] cursor-pointer items-center justify-center rounded-lg border text-center font-serif ${
                        textSettings.fontType.value === "sans-serif"
                          ? "border-primary dark:border-primary"
                          : "border-black dark:border-white"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          fontType: {
                            value: "sans-serif",
                            label: "Serif",
                          },
                        });
                      }}
                    />
                    <TranslatedText
                      text={`content-details.reader.mono-font`}
                      className={`flex h-[30px] w-[45px] cursor-pointer items-center justify-center rounded-lg border text-center font-mono ${
                        textSettings.fontType.value === "monospace"
                          ? "border-primary dark:border-primary"
                          : "border-black dark:border-white"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          fontType: {
                            value: "monospace",
                            label: "Mono",
                          },
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="flex w-full flex-col items-start justify-center gap-y-3">
                  <TranslatedText
                    text={`content-details.reader.background color`}
                  />
                  <div className="flex items-center justify-center gap-x-2">
                    <div
                      className={`h-[19px] w-[44px] cursor-pointer rounded-lg border bg-white text-center font-mono dark:hidden ${
                        !textSettings.backgroundColor
                          ? "border-primary"
                          : "border-white"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          backgroundColor: undefined,
                        });
                      }}
                    ></div>
                    <div
                      className={`hidden h-[19px] w-[44px] cursor-pointer rounded-lg border bg-black text-center font-mono dark:block ${
                        !textSettings.backgroundColor
                          ? "border-primary"
                          : "border-black"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          backgroundColor: undefined,
                        });
                      }}
                    ></div>
                    <div
                      className={`h-[19px] w-[44px] cursor-pointer rounded-lg border bg-[#141414] text-center font-mono ${
                        textSettings.backgroundColor === "#141414"
                          ? "border-primary"
                          : "border-[#141414]"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          backgroundColor: "#141414",
                        });
                      }}
                    ></div>
                    <div
                      className={`h-[19px] w-[44px] cursor-pointer rounded-lg border bg-[#2D2A23] text-center font-mono ${
                        textSettings.backgroundColor === "#2D2A23"
                          ? "border-primary"
                          : "border-[#2D2A23]"
                      }`}
                      onClick={() => {
                        onTextSettingsChange({
                          ...textSettings,
                          backgroundColor: "#2D2A23",
                        });
                      }}
                    ></div>
                  </div>
                </div>
              </MenubarItem>
            </MenubarSubContent>
          </MenubarSub>
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
};

interface HighlightRemoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  data: { id: number | undefined; text: string | undefined };
}

const HighlightRemoveModal = ({
  isOpen,
  onClose,
  onSubmit,
  data,
}: HighlightRemoveModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} contentClassName="w-[400px]">
      <div>
        <div className="text-center text-black dark:text-white">
          {data.text}
        </div>
        <div className="mt-6 flex items-center justify-center gap-4">
          <ShadcnButton
            className="!block bg-primary text-white hover:bg-primary/80"
            onClick={onSubmit}
          >
            <div>{"حذف التظليل"}</div>
          </ShadcnButton>
          <ShadcnButton
            className="!block bg-transparent text-black hover:bg-transparent dark:text-white"
            onClick={onClose}
          >
            {/* <TranslatedText
            text={`buttons.${"cancel"}`}
            className="text-black text-[16px] font-normal leading-[22px]"
          /> */}
            إلغاء
          </ShadcnButton>
        </div>
      </div>
    </Modal>
  );
};
